import React, { useState, useEffect } from 'react';
import VideoModal from '../../components/ui/VideoModal';
import Header from '../../components/ui/Header';
import './learning-center.css';

const LearningCenter = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState({ url: '', title: '' });

  const openVideo = (videoUrl, title) => {
    setCurrentVideo({ url: videoUrl, title });
    setIsVideoOpen(true);
  };

  const closeVideo = () => {
    setIsVideoOpen(false);
    setCurrentVideo({ url: '', title: '' });
  };

  // ESC key handler
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape' && isVideoOpen) {
        closeVideo();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isVideoOpen]);

  const modules = [
    {
      icon: '🏦',
      title: '1. The Philosophy of Financial Clarity',
      subtitle: '"See Before You Decide" (Video: 2:15 min)',
      content: 'Most people think managing money means saving more or investing smarter. In truth, it begins with **seeing clearly** — understanding where your money comes from and where it goes. RetireProApp is your mirror.',
      quote: '"Clarity always comes before confidence."',
      ctaText: '▶ Watch Video Tutorial',
      ctaType: 'video',
      videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ'
    },
    {
      icon: '💸',
      title: '2. Understanding Cash Flow',
      subtitle: '"Your Money Stream, Not a Snapshot" (Text Module)',
      content: 'Your cash flow is like a living river — income flows in, expenses flow out. **Positive cash flow builds assets; negative cash flow consumes them.** By tracking inflows and outflows, RetireProApp helps you see the trend consistently.',
      quote: '"Wealth isn\'t built by what you earn, it\'s built by what you keep — consistently."',
      ctaText: 'Read Full Guide',
      ctaType: 'text'
    },
    {
      icon: '🧱',
      title: '3. The 4× Margin of Safety Principle',
      subtitle: '"Plan for the Known Unknowns" (Video: 4:30 min)',
      content: 'Future risks — inflation, health costs, crises — are certain, but their timing is not. We use a **4× safety rule** so that if your plan says you need ₹1 Cr, your target is ₹4 Cr. This buffer ensures freedom from fear.',
      quote: '"Safety isn\'t about fear. It\'s about freedom from fear."',
      ctaText: '▶ Watch Video Tutorial',
      ctaType: 'video',
      videoUrl: 'https://www.youtube.com/watch?v=JiTz2i4VHFw'
    },
    {
      icon: '🧮',
      title: '4. The Risk Score',
      subtitle: '"Measure What Truly Matters" (Text Module)',
      content: 'Your Risk Score is a heartbeat monitor for your money, considering **Debt-to-Asset levels**, **Liquidity**, and **Income Variability**. It shows whether you\'re in the Green, Amber, or Red Zone, enabling early action.',
      quote: '"You can\'t control markets, but you can control your readiness."',
      ctaText: 'Read Full Guide',
      ctaType: 'text'
    },
    {
      icon: '📈',
      title: '5. Financial Independence vs. Net Worth',
      subtitle: '"The True Meaning of Enough" (Video: 3:05 min)',
      content: 'Net worth is what you have. Financial independence is what you can **sustain**. We calculate the **Income-to-Expense coverage ratio**—when assets cover needs indefinitely, you are free.',
      quote: '"Freedom isn\'t when you stop working. It\'s when work becomes a choice."',
      ctaText: '▶ Watch Video Tutorial',
      ctaType: 'video',
      videoUrl: 'https://www.youtube.com/watch?v=8cu4qAoeXow'
    },
    {
      icon: '⚙️',
      title: '6. The Flow of Wealth',
      subtitle: '"Surplus Builds, Deficit Teaches" (Video: 2:40 min)',
      content: 'We model the dynamic cycle of **Surplus years** (assets grow) and **Deficit years** (assets support you). Learn how the app automatically invests surplus and withdraws assets like a living ecosystem.',
      quote: '"Wealth is not static — it flows, like life. Learn to direct it, not fight it."',
      ctaText: '▶ Watch Video Tutorial',
      ctaType: 'video',
      videoUrl: 'https://www.youtube.com/watch?v=PHe0bXAIuk0'
    },
    {
      icon: '🛡️',
      title: '7. Inflation — The Invisible Tax',
      subtitle: '"Nominal Numbers Comfort. Real Numbers Protect." (Text Module)',
      content: 'Inflation slowly erodes the value of money. That\'s why every projection in RetireProApp is **inflation-adjusted**—you see the real purchasing power of your wealth, not just big nominal numbers.',
      quote: '"Nominal numbers comfort. Real numbers protect."',
      ctaText: 'Read Full Guide',
      ctaType: 'text'
    },
    {
      icon: '🔁',
      title: '8. Scenario Thinking',
      subtitle: '"The Art of \'What If\'" (Video: 5:10 min)',
      content: 'Good planning is about being prepared for possibilities. Simulate **Job loss**, **Market crash**, or **Early retirement** to test how resilient your plan truly is. Resilience is preparation in disguise.',
      quote: '"Resilience isn\'t luck — it\'s preparation in disguise."',
      ctaText: '▶ Watch Video Tutorial',
      ctaType: 'video',
      videoUrl: 'https://www.youtube.com/watch?v=TgOu87B9Jqg'
    }
  ];

  const futureModules = [
    'Asset Diversification Explained',
    'Emergency Fund Masterclass',
    'Understanding Liquidity',
    'Behavioral Biases in Financial Decisions',
    'How to Set Realistic Returns',
    'Reading Your Risk Score (Deep Dive)',
    '10 Money Myths That Break Retirement Plans'
  ];

  return (
    <>
      <Header />
      <div className="learning-center-page">
        {/* Hero Section */}
        <section className="learning-hero">
          <div className="learning-container">
            <h1>🧠 RetireProApp <span className="highlight">Learning Center</span></h1>
            <p>Understand the philosophy, principles, and tools behind achieving true financial confidence. Clarity always comes before confidence.</p>
          </div>
        </section>

      {/* Modules Grid */}
      <section className="learning-modules">
        <div className="learning-container">
          <div className="module-grid">
            {modules.map((module, index) => (
              <div key={index} className="module-card">
                <div className="module-body">
                  <div className="module-icon">{module.icon}</div>
                  <div className="module-title">
                    <h2>{module.title}</h2>
                    <span>{module.subtitle}</span>
                  </div>
                  <div className="module-content">
                    <p dangerouslySetInnerHTML={{ __html: module.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}></p>
                    <span className="quote">{module.quote}</span>
                  </div>
                </div>
                {module.ctaType === 'video' ? (
                  <button
                    onClick={() => openVideo(module.videoUrl, module.title)}
                    className="video-cta-button"
                    style={{ display: 'block', visibility: 'visible' }}
                  >
                    {module.ctaText}
                  </button>
                ) : (
                  <button
                    className="video-cta-button text-guide"
                    onClick={() => alert('Text guide coming soon!')}
                    style={{ display: 'block', visibility: 'visible' }}
                  >
                    {module.ctaText}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Future Modules */}
          <div className="future-modules">
            <h2>✨ Future Learning Modules (Phase 2)</h2>
            <ul>
              {futureModules.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoOpen}
        onClose={closeVideo}
        videoUrl={currentVideo.url}
        title={currentVideo.title}
      />
      </div>
    </>
  );
};

export default LearningCenter;

